import {
  Button, Checkbox,
  Container, Group, NumberInput,
  Select,
  Text,
  Textarea,
  TextInput,
  Title
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { type FormValidationResult, useForm } from "@mantine/form";
import { getServiceCategory } from "@packages/lib";
import { newCustomerSchema } from "@packages/schemas/customer";
import { newDealSchema } from "@packages/schemas/deal";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useCallback, useEffect, useMemo, useState } from "react";
import z from "zod";
import { AddressField, type IncompleteAddress } from "~/components/input-fields/address-field";
import { Section } from "~/components/view/section";
import { logger } from "~/lib/logger";
import { errorNotification, successNotification } from "~/lib/notifications";
import { trpc } from "~/lib/trpc";

export const Route = createFileRoute("/_authenticated/deals/new")({
  component: RouteComponent,
  validateSearch: z.object({
    customerId: z.number().optional(),
  }),
  beforeLoad: async ({ search: { customerId } }) => {
    return { customerId };
  },
  loader: async ({ context: { trpc, queryClient, customerId } }) => {
    const [dealFieldConfigs, customerFieldConfigs, customer] = await Promise.all([
      queryClient.ensureQueryData(trpc.crm.getFieldConfigsByTable.queryOptions({ table: "crm.deal" })),
      queryClient.ensureQueryData(trpc.crm.getFieldConfigsByTable.queryOptions({ table: "crm.customer" })),
      customerId ? queryClient.ensureQueryData(trpc.customer.getCustomer.queryOptions({ id: customerId })) : undefined,
    ]);

    const existingCustomer = customer
      ? {
        id: customer.id,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        identificationNumber: customer.identificationNumber,
        businessRepresentative: customer.businessRepresentative,
        type: customer.type,
      }
      : null;

    return {
      dealFieldConfigs,
      customerFieldConfigs,
      existingCustomer,
    };
  },
});

function RouteComponent() {
  const { dealFieldConfigs, customerFieldConfigs, existingCustomer } = Route.useLoaderData();
  const [address1, setAddress1] = useState<IncompleteAddress>();
  const [address2, setAddress2] = useState<IncompleteAddress>();
  const navigate = useNavigate();

  const dealForm = useForm({
    validate: zod4Resolver(newDealSchema),
    initialValues: {
      serviceDate: null as string | null,
      service: null as string | null,
      price: "",
      description: "",
      infoFromCustomer: "",
      addition: "",

      elevator1: "",
      elevator2: "",

      floor1: null as string | null,
      floor2: null as string | null,

      entryCode1: null as string | null,
      entryCode2: null as string | null,

      area1: null as number | null,
      area2: null as number | null,

      moms: null as boolean | null,
      rut: null as boolean | null,

      paymentMethod: "",
      source: null as string | null,
      status: "lead" as string,
    },
  });

  const customerForm = useForm({
    validate: zod4Resolver(newCustomerSchema),
    initialValues: {
      name: existingCustomer?.name || "",
      email: existingCustomer?.email || "",
      phone: existingCustomer?.phone || "",
      identificationNumber: existingCustomer?.identificationNumber || "",
      businessRepresentative: existingCustomer?.businessRepresentative || "",
      type: existingCustomer?.type || null,
    },
  });

  const createDeal = useMutation(
    trpc.deal.createDeal.mutationOptions({
      onSuccess: async (data) => {
        successNotification("Deal created successfully");
        await navigate({ to: "/deals/$dealId", params: { dealId: data.dealId.toString() } });
      },
      onError(error) {
        logger.error(error);
        errorNotification(error.message);
      },
    }),
  );

  useEffect(() => {
    const type = customerForm.values.type;
    if (!type) return;
    logger.debug({ type });
    dealForm.setValues({
      rut: type === "private",
      moms: type === "private",
    });
  }, [customerForm.values.type]);

  function onCreateDeal() {
    const validateDeal = dealForm.validate();

    let validateCustomer: FormValidationResult | null = null;

    if (!existingCustomer) {
      validateCustomer = customerForm.validate();
    }

    if (validateDeal.hasErrors || validateCustomer?.hasErrors) {
      logger.error("dealErrors", validateDeal.errors);
      logger.error("customerErrors", validateCustomer?.errors);
      errorNotification("Please fix the errors");
      return;
    }

    createDeal.mutate({
      customer: existingCustomer ? { customerId: existingCustomer.id } : newCustomerSchema.parse(customerForm.values),
      deal: newDealSchema.parse(dealForm.values),
      address1: {
        address: address1?.address || null,
        area: address1?.area || null,
        city: address1?.city || null,
        country: address1?.country || null,
        zip: address1?.zip || null,
        formattedAddress: address1?.formattedAddress || null,
        latitude: address1?.latitude || null,
        longitude: address1?.longitude || null,
        placeId: address1?.placeId || null,
        plusCode: address1?.plusCode || null,
        types: address1?.types || null,
      },
      address2: {
        address: address2?.address || null,
        area: address2?.area || null,
        city: address2?.city || null,
        country: address2?.country || null,
        zip: address2?.zip || null,
        formattedAddress: address2?.formattedAddress || null,
        latitude: address2?.latitude || null,
        longitude: address2?.longitude || null,
        placeId: address2?.placeId || null,
        plusCode: address2?.plusCode || null,
        types: address2?.types || null,
      },
    });
  }

  const getDealInputProps = useCallback(
    (field: keyof z.infer<typeof newDealSchema>) => {
      const config = dealFieldConfigs[field];
      const options = config?.config?.options?.map((op) => ({
        value: op.id,
        label: op.labelSv || op.labelEn || op.id,
        disabled: !op.isActive,
      }));

      const configuredLabel = config?.config?.labelSv || config?.config?.labelEn;

      return {
        label: (
          <Group>
            {configuredLabel ? (
              <Text fw={500} fz="sm">
                {configuredLabel}
              </Text>
            ) : (
              <Text c="dimmed" fz="md">
                {field}
              </Text>
            )}
          </Group>
        ),
        data: options,
        ...dealForm.getInputProps(field, {
          type: ["rut", "moms", "elevator1", "elevator2"].includes(field) ? "checkbox" : undefined,
        }),
      };
    },
    [dealForm, dealFieldConfigs],
  );

  const getCustomerInputProps = useCallback(
    (field: keyof z.infer<typeof newCustomerSchema>) => {
      const config = customerFieldConfigs[field];
      const options = config?.config?.options?.map((op) => ({
        value: op.id,
        label: op.labelSv || op.labelEn || op.id,
        disabled: !op.isActive,
      }));

      const configuredLabel = config?.config?.labelSv || config?.config?.labelEn;

      return {
        label: (
          <Group>
            {configuredLabel ? (
              <Text fw={500} fz="sm">
                {configuredLabel}
              </Text>
            ) : (
              <Text c="dimmed" fz="md">
                {field}
              </Text>
            )}
          </Group>
        ),
        disabled: !!existingCustomer,
        data: options,
        ...customerForm.getInputProps(field),
      };
    },
    [customerForm, customerFieldConfigs],
  );

  const isMovingService = useMemo(() => {
    return getServiceCategory(dealForm.values.service) === "moving";
  }, [dealForm.values.service]);

  return (
    <Container py="lg">
      <Title>Skapa ny affär</Title>
      {/* <Group grow>
        <pre>{JSON.stringify(dealForm.values, null, 2)}</pre>
        <pre>{JSON.stringify(customerForm.values, null, 2)}</pre>
      </Group> */}
      <Section title="Kund" mt="xl">
        <Section.Col width="50%">
          <TextInput {...getCustomerInputProps("name")} />
        </Section.Col>
        <Section.Col width="50%">
          <TextInput {...getCustomerInputProps("identificationNumber")} />
        </Section.Col>
        <Section.Col width="50%">
          <TextInput {...getCustomerInputProps("email")} />
        </Section.Col>
        <Section.Col width="50%">
          <TextInput {...getCustomerInputProps("phone")} />
        </Section.Col>
        <Section.Col width="50%">
          <Select {...getCustomerInputProps("type")} />
        </Section.Col>
        {customerForm.values.type === "business" && (
          <Section.Col width="50%">
            <TextInput {...getCustomerInputProps("businessRepresentative")} />
          </Section.Col>
        )}
      </Section>
      <Section title="Affär" mt="xl">
        <Section.Col width="50%">
          <DateInput {...getDealInputProps("serviceDate")} />
        </Section.Col>
        <Section.Col width="50%">
          <Select {...getDealInputProps("source")} />
        </Section.Col>
        <Section.Col width="50%">
          <Select {...getDealInputProps("service")} />
        </Section.Col>
        <Section.Col width="100%">
          <Textarea autosize {...getDealInputProps("description")} />
        </Section.Col>
        <Section.Col width="100%">
          <Textarea autosize {...getDealInputProps("infoFromCustomer")} />
        </Section.Col>
        <Section.Col width="25%">
          <Checkbox {...getDealInputProps("rut")} indeterminate={dealForm.values.rut === null} />
        </Section.Col>
        <Section.Col width="25%">
          <Checkbox {...getDealInputProps("moms")} indeterminate={dealForm.values.moms === null} />
        </Section.Col>
      </Section>

      {/* address 1 */}
      <Section title={`Address ${isMovingService ? "from" : ""}`} mt="xl">
        <Section.Col width="100%">
          <AddressField label="Address" onSelect={(a) => setAddress1(a)} createWhenEmpty={{ action: false }} />
        </Section.Col>
        <Section.Col width="33%">
          <TextInput {...getDealInputProps("floor1")} />
        </Section.Col>
        <Section.Col width="33%">
          <NumberInput {...getDealInputProps("area1")} decimalScale={0} />
        </Section.Col>
        <Section.Col width="33%">
          <TextInput {...getDealInputProps("entryCode1")} />
        </Section.Col>
        <Section.Col width="33%">
          <TextInput {...getDealInputProps("elevator1")} />
        </Section.Col>
      </Section>

      {/* address 2 */}
      {isMovingService && (
        <Section title="Address to" mt="xl">
          <Section.Col width="100%">
            <AddressField label="Address" onSelect={(a) => setAddress2(a)} createWhenEmpty={{ action: false }} />
          </Section.Col>
          <Section.Col width="33%">
            <TextInput {...getDealInputProps("floor2")} />
          </Section.Col>
          <Section.Col width="33%">
            <NumberInput {...getDealInputProps("area2")} decimalScale={0} />
          </Section.Col>
          <Section.Col width="33%">
            <TextInput {...getDealInputProps("entryCode2")} />
          </Section.Col>
          <Section.Col width="33%">
            <TextInput {...getDealInputProps("elevator2")} />
          </Section.Col>
        </Section>
      )}
      <Group justify="flex-end">
        <Button onClick={onCreateDeal}>Create</Button>
      </Group>
    </Container>
  );
}
